import React, { FC, useEffect, useMemo, useState } from 'react';
import { Col, Layout, Row, Segmented, Select } from 'antd';
import { PageHeader } from 'common/components/Page/PageHeader';
import { PageContent } from 'common/components/Page/PageContent';
import { PageStoreSelector } from 'common/components/PageStoreSelector';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { getTrackStoreStoreCollection } from 'app/store/reducers/trackStore.reducer';
import { getStockReconciliationShiftCollection } from 'app/store/reducers/stockReconciliation.reducer';
import { EStockReconciliationUoM } from 'entities/StockReconciliation/StockReconciliation.models';
import {
  STOCK_RECONCILIATION_SHIFTS_LIMIT,
  stockReconciliationUomOptions,
} from 'entities/StockReconciliation/StockReconciliation.const';
import { stockReconciliationWeekOptions } from 'entities/StockReconciliation/StockReconciliation.helper';
import { StockReconciliationShiftsTable } from 'entities/StockReconciliation/components/StockReconciliationShiftsTable';

export const StockReconciliationPage: FC = () => {
  const dispatch = useAppDispatch();
  const { trackStoreStoreCollection } = useAppSelector((state) => state.trackStore);

  const [storeCode, setStoreCode] = useState<string | number | undefined>();
  const [week, setWeek] = useState<string>('currentWeek');
  const [uom, setUom] = useState<EStockReconciliationUoM>(EStockReconciliationUoM.Packs);
  const [page, setPage] = useState<number>(1);

  const selectedStore = useMemo(
    () => trackStoreStoreCollection?.data.find((item) => item.code === storeCode),
    [trackStoreStoreCollection?.data, storeCode]
  );

  const weekOptions = useMemo(() => stockReconciliationWeekOptions(), []);

  useEffect(() => {
    dispatch(getTrackStoreStoreCollection());
  }, [dispatch]);

  useEffect(() => {
    setPage(1);
  }, [storeCode, week, uom]);

  useEffect(() => {
    if (!selectedStore?.code || !selectedStore?.location) {return;}
    dispatch(
      getStockReconciliationShiftCollection({
        storeCode: selectedStore.code,
        location: selectedStore.location,
        week,
        uom,
        offset: (page - 1) * STOCK_RECONCILIATION_SHIFTS_LIMIT,
        limit: STOCK_RECONCILIATION_SHIFTS_LIMIT,
      })
    );
  }, [selectedStore, week, uom, page, dispatch]);

  return (
    <Layout className="relative height-full flex flex__column">
      <PageHeader
        title={<span>Stock Reconciliation</span>}
        extra={
          <Row gutter={12} align="middle">
            <Col>
              <PageStoreSelector stores={trackStoreStoreCollection?.data} onChange={setStoreCode} />
            </Col>
            <Col>
              <Select
                value={week}
                options={weekOptions}
                onChange={setWeek}
                style={{ minWidth: 220 }}
              />
            </Col>
            <Col>
              <Segmented
                options={stockReconciliationUomOptions}
                value={uom}
                onChange={(value) => setUom(value as EStockReconciliationUoM)}
              />
            </Col>
          </Row>
        }
      />

      <PageContent>
        <div className="flex flex__column flex__item_1 page-content__scroll-container scrollbar-none p-5">
          {selectedStore ? (
            <StockReconciliationShiftsTable uom={uom} page={page} onPageChange={setPage} />
          ) : (
            <div className="color-gray-1">Select a store to see shifts for the week.</div>
          )}
        </div>
      </PageContent>
    </Layout>
  );
};
